/**
 * Progresso da Ajuda do Elos, guardado NESTE APARELHO (localStorage).
 *
 * Decisão de produto (v1): não há tabela nem migration. O progresso é só uma
 * conveniência ("lição vista", "cartão dispensado"); trocar de aparelho ou
 * limpar os dados do navegador recomeça do zero, sem prejuízo. Se um dia for
 * para a conta, só este módulo e o hook `useProgressoAjuda` mudam.
 *
 * Regras puras e tolerantes a falha: o localStorage pode estar vazio,
 * bloqueado (janela privativa) ou com lixo — nada disso pode quebrar a tela.
 */

export interface ProgressoAjuda {
  /** ids das lições já concluídas. */
  vistas: string[];
  /** a pessoa fechou o cartão "Novo por aqui?" do Início. */
  cartaoDispensado: boolean;
  /** a pessoa já respondeu à tela de boas-vindas (Começar ou Pular por agora). */
  boasVindasVista: boolean;
  /** ids das lições cujo cartão do Início ("Novo para quem lidera"...) foi fechado com "Agora não". */
  cartoesDispensados: string[];
}

export const PROGRESSO_VAZIO: ProgressoAjuda = {
  vistas: [],
  cartaoDispensado: false,
  boasVindasVista: false,
  cartoesDispensados: [],
};

type LeitorStorage = Pick<Storage, "getItem">;
type GravadorStorage = Pick<Storage, "setItem">;

/** Uma chave por pessoa: quem divide o aparelho não herda o progresso do outro. */
export function chaveProgresso(pessoaId: string): string {
  return `elos:ajuda:v1:${pessoaId}`;
}

export function lerProgresso(storage: LeitorStorage | null, chave: string): ProgressoAjuda {
  if (!storage) return PROGRESSO_VAZIO;
  try {
    const bruto = storage.getItem(chave);
    if (!bruto) return PROGRESSO_VAZIO;
    const dado: unknown = JSON.parse(bruto);
    if (typeof dado !== "object" || dado === null) return PROGRESSO_VAZIO;
    const { vistas, cartaoDispensado, boasVindasVista, cartoesDispensados } = dado as Record<
      string,
      unknown
    >;
    const soTextos = (v: unknown) =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    return {
      vistas: soTextos(vistas),
      cartaoDispensado: cartaoDispensado === true,
      boasVindasVista: boasVindasVista === true,
      cartoesDispensados: soTextos(cartoesDispensados),
    };
  } catch {
    return PROGRESSO_VAZIO;
  }
}

export function gravarProgresso(
  storage: GravadorStorage | null,
  chave: string,
  progresso: ProgressoAjuda,
): void {
  if (!storage) return;
  try {
    storage.setItem(chave, JSON.stringify(progresso));
  } catch {
    // Sem espaço ou bloqueado: a Ajuda continua funcionando, só não lembra.
  }
}

export function marcarVista(progresso: ProgressoAjuda, licaoId: string): ProgressoAjuda {
  if (progresso.vistas.includes(licaoId)) return progresso;
  return { ...progresso, vistas: [...progresso.vistas, licaoId] };
}

export function dispensarCartao(progresso: ProgressoAjuda): ProgressoAjuda {
  return progresso.cartaoDispensado ? progresso : { ...progresso, cartaoDispensado: true };
}

/** Fecha o cartão do Início de uma lição ("Agora não"). A lição continua na Ajuda. */
export function dispensarCartaoDaLicao(progresso: ProgressoAjuda, licaoId: string): ProgressoAjuda {
  if (progresso.cartoesDispensados.includes(licaoId)) return progresso;
  return { ...progresso, cartoesDispensados: [...progresso.cartoesDispensados, licaoId] };
}

export function marcarBoasVindas(progresso: ProgressoAjuda): ProgressoAjuda {
  return progresso.boasVindasVista ? progresso : { ...progresso, boasVindasVista: true };
}

/**
 * A tela de boas-vindas aparece uma vez por pessoa e por aparelho (o Elos não guarda isso na
 * conta): só enquanto ela não respondeu à tela, não viu nenhuma lição e não fechou o cartão.
 */
export function deveMostrarBoasVindas(progresso: ProgressoAjuda): boolean {
  return !progresso.boasVindasVista && !progresso.cartaoDispensado && progresso.vistas.length === 0;
}

/** Quantas das lições atuais já foram vistas (ignora ids que não existem mais). */
export function contarVistas(progresso: ProgressoAjuda, idsLicoes: string[]): number {
  return idsLicoes.filter((id) => progresso.vistas.includes(id)).length;
}

/** O cartão do Início some se foi dispensado ou se a pessoa já viu tudo. */
export function deveMostrarCartao(progresso: ProgressoAjuda, idsLicoes: string[]): boolean {
  if (progresso.cartaoDispensado) return false;
  return contarVistas(progresso, idsLicoes) < idsLicoes.length;
}
